import type { Pair } from './statsApi'

/*
 * Числа и даты для страницы статистики. Отдельным модулем: их зовут и графики, и страница,
 * а файлы компонентов должны экспортировать только компоненты.
 */
const intFmt = new Intl.NumberFormat('ru-RU')
const compactFmt = new Intl.NumberFormat('ru-RU', { notation: 'compact', maximumFractionDigits: 1 })

export const fmtInt = (n: number): string => intFmt.format(Math.round(n))

/** До 10 000 — полностью, дальше — «12,9 тыс.». */
export const fmtCompact = (n: number): string => (Math.abs(n) < 10_000 ? fmtInt(n) : compactFmt.format(n))

/** Минуты в часах: до десяти — с десятыми. */
export const fmtHours = (minutes: number): string => {
  const h = minutes / 60
  return h < 10 ? h.toLocaleString('ru-RU', { maximumFractionDigits: 1 }) : fmtInt(h)
}

export const pct = (part: number, whole: number): number => (whole > 0 ? Math.round((part / whole) * 100) : 0)

/** «12 сен» — для осей и заголовков. */
export function shortDay(day: string): string {
  return new Date(`${day}T12:00:00`).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }).replace('.', '')
}

/** «пн, 22 сентября» — для подсказки. */
export function longDay(day: string): string {
  return new Date(`${day}T12:00:00`).toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'long' })
}

/** Верх оси: ближайшее «круглое» число не меньше максимума (1, 2, 2,5, 5 × 10ⁿ). */
export function niceMax(v: number): number {
  if (v <= 0) return 1
  const p = 10 ** Math.floor(Math.log10(v))
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= v) return m * p
  return 10 * p
}

export type Direction = 'up' | 'down' | 'flat' | 'new' | 'none'

/** Изменение к прошлому окну: для всех наших цифр рост — хорошо. */
export function change(p: Pair): { dir: Direction; text: string } {
  if (p.now === 0 && p.prev === 0) return { dir: 'none', text: 'пока нет' }
  if (p.prev === 0) return { dir: 'new', text: 'раньше не было' }
  const d = Math.round(((p.now - p.prev) / p.prev) * 100)
  if (d === 0) return { dir: 'flat', text: 'как раньше' }
  return { dir: d > 0 ? 'up' : 'down', text: `${d > 0 ? '+' : '−'}${Math.abs(d)}%` }
}

/** Склонение: 1 сервер, 2 сервера, 5 серверов — вместе с числом. */
export function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  const word = mod10 === 1 && mod100 !== 11 ? one : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? few : many
  return `${fmtInt(n)} ${word}`
}

/** Сколько дней прошло от day до today (оба — «2026-09-24»): «сегодня», «вчера», «3 дня назад», дальше — дата. */
export function daysAgo(day: string, today: string): string {
  const d = Math.round((Date.parse(`${today}T12:00:00Z`) - Date.parse(`${day}T12:00:00Z`)) / 86_400_000)
  if (d <= 0) return 'сегодня'
  if (d === 1) return 'вчера'
  if (d < 7) return `${plural(d, 'день', 'дня', 'дней')} назад`
  return shortDay(day)
}

/** Дата по Москве из метки времени: «12 сен 2026». */
export function mskDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { timeZone: 'Europe/Moscow', day: 'numeric', month: 'short', year: 'numeric' }).replace(' г.', '')
}
